import { STATUS_LABELS, STATUS_TONES } from '@bedouin/shared'

/** Human label for any status value, falling back to a readable form. */
export function statusLabel(value: string | null | undefined): string {
  if (!value) return '-'
  return STATUS_LABELS[value] ?? value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

/** Bulma colour modifier for a status, so one status looks the same anywhere. */
export function statusTone(value: string | null | undefined): string {
  if (!value) return 'light'
  return STATUS_TONES[value] ?? 'light'
}

/**
 * Calendar dates (`YYYY-MM-DD`) are read as midnight UTC, so they must be
 * formatted in UTC too. Formatting in the viewer's zone moved every date a
 * day earlier anywhere west of Greenwich -- 20 Aug showed as 19 Aug in Los
 * Angeles. A due date is a day on the calendar, not an instant; no zone
 * should change it.
 */
const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
})

/**
 * Calendar dates are rendered in UTC, not the viewer's zone. A due date of
 * `2026-03-04` is that day everywhere -- shifting it by timezone would show
 * the 3rd to anyone west of UTC.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  const parsed = Date.parse(`${value.slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(parsed)) return value
  return DATE_FORMAT.format(new Date(parsed))
}

/** Audit timestamps are moments in time, so these do use the local zone. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`
  const parsed = Date.parse(normalized)
  if (Number.isNaN(parsed)) return value
  return DATE_TIME_FORMAT.format(new Date(parsed))
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return ''
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`
  const parsed = Date.parse(normalized)
  if (Number.isNaN(parsed)) return ''

  const seconds = Math.round((Date.now() - parsed) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`
  return formatDate(new Date(parsed).toISOString())
}

/** Spot lengths read as `:30` or `1:30` in this industry, not "30 seconds". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '-'
  if (seconds < 60) return `:${String(seconds).padStart(2, '0')}`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}

export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount === null || amount === undefined) return '-'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(amount)
  } catch {
    // An unknown currency code should not blank the number out.
    return `${currency ?? ''} ${amount.toLocaleString()}`.trim()
  }
}

/** How a due date should read: overdue, due soon, or neither. */
export function dueTone(days: number | null | undefined): '' | 'is-overdue' | 'is-soon' {
  if (days === null || days === undefined) return ''
  if (days < 0) return 'is-overdue'
  if (days <= 3) return 'is-soon'
  return ''
}

export function dueNote(days: number | null | undefined): string {
  if (days === null || days === undefined) return ''
  if (days < 0) return `${Math.abs(days)}d late`
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days <= 14) return `in ${days}d`
  return ''
}

/**
 * Today as the viewer's own calendar shows it, `YYYY-MM-DD`. Taking it from
 * the UTC clock made "due today" read as overdue every evening in the
 * Americas. Sent to the API as `?today=` wherever it decides what is overdue.
 */
export function localToday(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Days between the viewer's today and an ISO date. */
export function daysUntil(date: string | null | undefined): number | null {
  if (!date) return null
  const target = Date.parse(`${date.slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(target)) return null
  const today = Date.parse(`${localToday()}T00:00:00Z`)
  return Math.round((target - today) / 86_400_000)
}

export const pluralize = (n: number, singular: string, plural = `${singular}s`): string =>
  `${n} ${n === 1 ? singular : plural}`
